// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { errorResponseSchema } from '@/api/types';
import { AxiosError, AxiosResponse } from 'axios';
import { z } from 'zod';

const HTTP_STATUS_MIN = 100;
// Upper bound is inclusive of 600 to mirror the backend Wrapper.GetStatusCode, which clamps only above 600
const HTTP_STATUS_MAX = 600;
const SERVICE_UNAVAILABLE_MIN = 900;
const SERVICE_UNAVAILABLE_MAX = 999;
const HTTP_SERVICE_UNAVAILABLE = 503;
const HTTP_BAD_REQUEST = 400;

/**
 * Response types whose body is never a JSON envelope. The guard cannot inspect
 * them, so it declares that limitation rather than silently passing them
 * through: `keepalive.Start` is JSON-only on the backend for the same reason,
 * and a heartbeat on a download would corrupt the file rather than produce an
 * envelope this could read.
 */
const NON_JSON_RESPONSE_TYPES = new Set([
  'blob',
  'arraybuffer',
  'stream',
  'document',
]);

/**
 * The envelope's own status code, which is the only truthful one on a
 * heartbeat-committed response.
 *
 * A long-running endpoint streams heartbeat bytes to survive the load-balancer
 * idle timeout, and the first heartbeat commits HTTP 200 before the handler
 * outcome is known. The HTTP status on such a response is therefore meaningless
 * and MUST NOT be read. `metadata.statusCode` mirrors the backend's
 * `Wrapper.Metadata.StatusCode`, which `AddError` overwrites on every call, so
 * it already encodes the same "last error wins" selection the backend would
 * have put on the status line. Deriving the status from `errors[0]` instead
 * disagrees with the backend whenever a response carries more than one error —
 * which the deliver endpoint does routinely, because `RequireTx` joins a
 * rollback failure onto the original error.
 */
const envelopeStatusSchema = z.object({
  metadata: z.object({ statusCode: z.number().int() }),
});

/**
 * Maps an envelope status code onto a plausible HTTP status, mirroring the
 * backend Wrapper.GetStatusCode clamping so downstream error handling sees
 * the same status it would have on a non-heartbeat response.
 */
function toHTTPStatus(statusCode: number): number {
  if (statusCode >= HTTP_STATUS_MIN && statusCode <= HTTP_STATUS_MAX) {
    return statusCode;
  }
  if (
    statusCode >= SERVICE_UNAVAILABLE_MIN &&
    statusCode <= SERVICE_UNAVAILABLE_MAX
  ) {
    return HTTP_SERVICE_UNAVAILABLE;
  }
  return HTTP_BAD_REQUEST;
}

/** Reports whether the server labelled this response as JSON. */
function isJSONResponse(response: AxiosResponse): boolean {
  const contentType: unknown = response.headers['content-type'];
  return typeof contentType === 'string' && contentType.includes('json');
}

function envelopeError(
  message: string,
  response: AxiosResponse,
  status: number
): AxiosError {
  return new AxiosError(
    message,
    AxiosError.ERR_BAD_RESPONSE,
    response.config,
    response.request,
    { ...response, status }
  );
}

/**
 * Throws an AxiosError when a 2xx response did not actually succeed.
 *
 * Long-running endpoints stream heartbeat bytes to keep the connection alive,
 * which commits HTTP 200 before the handler outcome is known; a later failure
 * then arrives in metadata.errors on an otherwise-successful response. Throwing
 * a real AxiosError routes it through the normal API error handling path as if
 * axios had rejected.
 *
 * Two shapes are caught:
 *  - a complete envelope carrying errors, whose status comes from
 *    metadata.statusCode (never from the HTTP status, and never from errors[0])
 *  - a JSON-labelled body that failed to parse, which axios leaves as a raw
 *    string. That means the response was truncated after the heartbeat
 *    committed 200 — a transport failure, not schema drift, and reporting it
 *    as the latter produces a false contract-drift alert
 *
 * Non-envelope bodies and non-JSON response types pass through untouched.
 */
export function throwIfEnvelopeError(response: AxiosResponse): AxiosResponse {
  const responseType = response.config.responseType;
  if (responseType !== undefined && NON_JSON_RESPONSE_TYPES.has(responseType)) {
    return response;
  }

  // Axios leaves an unparseable body as the raw string; on a JSON-labelled
  // response that is a truncated or aborted body, not a non-envelope one.
  if (typeof response.data === 'string' && isJSONResponse(response)) {
    throw envelopeError(
      'API response body was not valid JSON',
      response,
      HTTP_SERVICE_UNAVAILABLE
    );
  }

  const result = errorResponseSchema.safeParse(response.data);
  if (!result.success || result.data.metadata.errors.length === 0) {
    return response;
  }

  const envelopeStatus = envelopeStatusSchema.safeParse(response.data);
  const statusCode = envelopeStatus.success
    ? envelopeStatus.data.metadata.statusCode
    : result.data.metadata.errors[0].statusCode;

  throw envelopeError(
    'API response envelope contained errors',
    response,
    toHTTPStatus(statusCode)
  );
}
